import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button, LoadingOrbit, Text } from '@chatsdet/ui';
import { CheckCircle, Warning } from '@phosphor-icons/react';
import type { GenerationEvent } from '../hooks/useWorkflowGenerationEvents';

type Props = {
  events: GenerationEvent[];
  /**
   * Workflow DB status. Used only to pick descriptive subtext while an author
   * translates source material. Modal mount is gated by
   * the parent's `isAuthoring`, not this prop.
   */
  workflowStatus: string;
  onRetry?: () => void;
  isRetrying?: boolean;
  retryError?: string | null;
};

type PhaseView = {
  icon: React.ReactNode;
  headline: string;
  subtext: string;
};

/**
 * Map the latest generation event + workflow status to the icon/copy the
 * modal should render. Only a `'completed'` event counts as positive
 * terminal — anything else (including no events at all) stays "in progress"
 * with the spinner. Pre-fix, the modal showed a green check + "Done."
 * whenever workflowStatus wasn't 'generating', which produced a misleading
 * "Starting up… / Done." pair for flows that never wrote terminal events.
 */
function phaseView(latest: GenerationEvent | null, workflowStatus: string): PhaseView {
  if (latest?.kind === 'failed' && workflowStatus === 'generating') {
    return {
      icon: (
        <div className="flex-shrink-0 mt-0.5">
          <LoadingOrbit size="md" />
        </div>
      ),
      headline: 'Retrying conversion…',
      subtext: 'Reading the original Selenium revision again…',
    };
  }
  if (latest?.kind === 'failed' && workflowStatus !== 'generating') {
    return {
      icon: <Warning size={22} weight="fill" className="text-destructive flex-shrink-0 mt-0.5" />,
      headline: 'Generation failed',
      subtext: 'Retry the conversion. Any existing flow steps are unchanged.',
    };
  }
  if (latest?.kind === 'completed') {
    return {
      icon: <CheckCircle size={22} weight="fill" className="text-green-500 flex-shrink-0 mt-0.5" />,
      headline: latest.message,
      subtext: 'Done.',
    };
  }
  return {
    icon: (
      <div className="flex-shrink-0 mt-0.5">
        <LoadingOrbit size="md" />
      </div>
    ),
    headline: latest?.message ?? 'Starting up…',
    subtext:
      workflowStatus === 'generating' ? 'Translating source material into a workflow…' : 'Working…',
  };
}

/**
 * Locked overlay shown while an author agent is building a workflow. Any
 * agent that writes to workflow_generation_events drives this UI — events
 * arrive newest-last and we render the most recent as the headline with a
 * short history beneath it.
 */
export const GenerationInterstitial: React.FC<Props> = ({
  events,
  workflowStatus,
  onRetry,
  isRetrying = false,
  retryError = null,
}) => {
  const latest = events[events.length - 1] ?? null;
  const view = phaseView(latest, workflowStatus);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
      className="absolute inset-0 z-30 flex items-center justify-center bg-background/60 backdrop-blur-sm"
      role="dialog"
      aria-live="polite"
      aria-label="Generating workflow"
    >
      <div className="w-[460px] max-w-[90%] rounded-xl border border-border bg-background p-5 flex flex-col gap-3">
        <div className="flex items-start gap-3">
          {view.icon}
          <div className="min-w-0 flex-1">
            <Text weight="semibold" className="text-base break-words">
              {view.headline}
            </Text>
            <Text className="text-xs text-muted-foreground">{view.subtext}</Text>
          </div>
        </div>

        {latest?.kind === 'failed' && workflowStatus !== 'generating' && onRetry ? (
          <div className="flex flex-col items-end gap-2 border-t border-border pt-3">
            {retryError ? (
              <Text className="self-start text-xs text-destructive">{retryError}</Text>
            ) : null}
            <Button size="sm" onPress={onRetry} isDisabled={isRetrying}>
              {isRetrying ? <LoadingOrbit size="sm" /> : null}
              Retry conversion
            </Button>
          </div>
        ) : null}

        {events.length > 1 && (
          <div className="flex flex-col gap-1 max-h-40 overflow-y-auto border-t border-border pt-3">
            <AnimatePresence initial={false}>
              {events
                .slice(0, -1)
                .slice(-6)
                .map((ev) => (
                  <motion.div
                    key={ev.id}
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 0.7, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.12 }}
                    className="text-xs text-muted-foreground"
                  >
                    · {ev.message}
                  </motion.div>
                ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </motion.div>
  );
};
